import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"

// v1.1.1-b3 / v1.4.6(1) PR-G:
//  - swipeDirection="up" enables touch-friendly swipe-to-dismiss that
//    matches the new below-header anchor: the toast slides up and off
//    under the header. Radix's Toast primitive supports this natively;
//    the data-[swipe=*]:translate-y-* classes in ui/toast.tsx do the move.
//  - swipeThreshold lowered to 40px so a small flick dismisses instead
//    of needing a full container-height drag.
//  - duration={5000} sets a 5-second auto-dismiss so toasts don't pile
//    up. Individual callers can still override per toast via props.
//  - ToastClose is rendered only where it is LOAD-BEARING — see below.
export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider swipeDirection="up" swipeThreshold={40} duration={5000}>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        /**
         * The dismiss X only appears on a toast that will NOT go away by
         * itself — one holding a decision (duration: Infinity, e.g. "Mark this
         * caught?") or carrying actions.
         *
         * Every other toast is a transient confirmation that auto-dismisses in
         * five seconds, and a close button on one of those is a control asking
         * to be used for something that is already handled: it draws the eye to
         * the corner of a message whose whole job is to be glanced at and
         * ignored. Swipe-to-dismiss still works on all of them, so nothing
         * becomes unclosable — the affordance just stops shouting.
         */
        const needsClose = props.duration === Infinity || Boolean(action)
        return (
          <Toast key={id} {...props}>
            <div className="grid gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            {needsClose && (
              <ToastClose className="opacity-100" aria-label="Dismiss notification" />
            )}
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
